import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface SocialEmbed {
  id: string;
  platform: string;
  embed_url: string;
  is_active: boolean;
  display_order: number;
}

interface SocialMediaEmbedsProps {
  userId: string;
  userType: 'coach' | 'team_owner' | 'venue' | 'player';
  editable?: boolean;
}

export const SocialMediaEmbeds = ({ userId, userType, editable = false }: SocialMediaEmbedsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [embeds, setEmbeds] = useState<SocialEmbed[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newEmbed, setNewEmbed] = useState({
    platform: 'twitter',
    embed_url: ''
  });

  useEffect(() => {
    loadEmbeds();
  }, [userId]);

  const loadEmbeds = async () => {
    try {
      const { data, error } = await supabase
        .from('social_media_embeds')
        .select('*')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setEmbeds(data || []);
    } catch (error) {
      console.error('Error loading embeds:', error);
    } finally {
      setLoading(false);
    }
  };

  const addEmbed = async () => {
    if (!newEmbed.embed_url) {
      toast({
        title: "Error",
        description: "Please enter a URL",
        variant: "destructive",
      });
      return;
    }

    // Validate URL format
    try {
      new URL(newEmbed.embed_url);
    } catch {
      toast({
        title: "Error",
        description: "Please enter a valid URL",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get current user ID - try Supabase auth first, then internal session
      let currentUserId = user?.id;
      
      if (!currentUserId) {
        const internalSession = localStorage.getItem('internal_user_session');
        if (internalSession) {
          try {
            const parsed = JSON.parse(internalSession);
            currentUserId = parsed.userId || parsed.user_id || parsed.id;
          } catch {
            // Ignore parse errors
          }
        }
      }

      if (!currentUserId) {
        toast({
          title: "Error",
          description: "Please log in to add social media links",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.from('social_media_embeds').insert({
        user_id: userId,
        user_type: userType,
        platform: newEmbed.platform,
        embed_url: newEmbed.embed_url,
        display_order: embeds.length + 1
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Social media link added",
      });

      setNewEmbed({ platform: 'twitter', embed_url: '' });
      setAdding(false);
      loadEmbeds();
    } catch (error: any) {
      console.error('Error adding embed:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add social media link",
        variant: "destructive",
      });
    }
  };

  const removeEmbed = async (embedId: string) => {
    try {
      const { error } = await supabase
        .from('social_media_embeds')
        .delete()
        .eq('id', embedId);

      if (error) throw error;

      toast({
        title: "Removed",
        description: "Social media link removed",
      });

      loadEmbeds();
    } catch (error) {
      console.error('Error removing embed:', error);
      toast({
        title: "Error",
        description: "Failed to remove social media link",
        variant: "destructive",
      });
    }
  };

  const getPlatformIcon = (platform: string) => {
    const icons = {
      twitter: '𝕏',
      instagram: '📷',
      facebook: '👥',
      youtube: '▶️',
      tiktok: '🎵'
    };
    return icons[platform] || '🔗';
  };

  const getPlatformColor = (platform: string) => {
    const colors = {
      twitter: 'bg-blue-600/20 text-blue-500 border-blue-600/30',
      instagram: 'bg-pink-600/20 text-pink-500 border-pink-600/30',
      facebook: 'bg-blue-700/20 text-blue-400 border-blue-700/30',
      youtube: 'bg-red-600/20 text-red-500 border-red-600/30',
      tiktok: 'bg-purple-600/20 text-purple-500 border-purple-600/30'
    };
    return colors[platform] || 'bg-gray-600/20 text-gray-400 border-gray-600/30';
  };

  if (loading) {
    return <div className="text-center text-gray-400 py-4">Loading social links...</div>;
  }

  return (
    <Card className="bg-gray-900 border-gray-700">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-white">Social Media</CardTitle>
          {editable && !adding && (
            <Button
              size="sm"
              onClick={() => setAdding(true)}
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Link
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add New Embed Form */}
        {adding && editable && (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 space-y-3">
              <div className="space-y-2">
                <Label htmlFor="platform" className="text-gray-300">Platform</Label>
                <Select
                  value={newEmbed.platform}
                  onValueChange={(value) => setNewEmbed({ ...newEmbed, platform: value })}
                >
                  <SelectTrigger className="bg-gray-900 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twitter">Twitter / X</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="url" className="text-gray-300">Profile URL</Label>
                <Input
                  id="url"
                  value={newEmbed.embed_url}
                  onChange={(e) => setNewEmbed({ ...newEmbed, embed_url: e.target.value })}
                  placeholder="https://..."
                  className="bg-gray-900 border-gray-700"
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={addEmbed} className="flex-1">
                  Add
                </Button>
                <Button onClick={() => setAdding(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Embed List */}
        {embeds.length === 0 && !adding ? (
          <div className="text-center py-8 text-gray-400">
            <p>No social media links added yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {embeds.map((embed) => (
              <div
                key={embed.id}
                className="flex items-center justify-between p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1">
                  <Badge className={getPlatformColor(embed.platform)} variant="outline">
                    {getPlatformIcon(embed.platform)}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium capitalize">{embed.platform}</p>
                    <p className="text-sm text-gray-400 truncate">{embed.embed_url}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => window.open(embed.embed_url, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  {editable && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeEmbed(embed.id)}
                      className="text-red-500 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
